import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo } from 'react'

import { formatOptionsList } from '../formatters'

import { selectedFacebookAdAccountSelector } from '../../modules/selectors/adAccounts'
import { choicesPixelsSelector, choicesPixelsWasLoadedSelector } from '../../modules/selectors/choices'
import { pixelStatsSelector } from '../../modules/selectors/lineItems'
import { clearChoicesPixels, getChoicesPixels } from '../../modules/actions/choices'
import { clearPixelStats, getPixelStats } from '../../modules/actions/lineItems'

import { FACEBOOK_PLATFORM } from '../../constants/selectLists/platformList'
import customEventTypes, {
  ADD_PAYMENT_INFO,
  ADD_TO_CART,
  ADD_TO_WISHLIST,
  CUSTOMIZE_PRODUCT,
  DONATE,
  eventTypes,
  INITIATED_CHECKOUT,
  PURCHASE
} from '../../constants/selectLists/customEventTypes'
import { OUTCOME_LEADS, OUTCOME_SALES } from '../../constants/campaigns'

const statsItemsToExclude = [
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'PageView',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'SubscribedButtonClick',
  'Subscribe',
  'ViewContent'
]

// if the campaign objective is leads, then disable (but still show) these in the dropdown
const disabledLeadsObjectiveOptions = [
  INITIATED_CHECKOUT,
  ADD_PAYMENT_INFO,
  ADD_TO_CART,
  ADD_TO_WISHLIST,
  CUSTOMIZE_PRODUCT,
  DONATE,
  PURCHASE
]

export const getFormattedPixelStats = pixelStats => {
  const list = pixelStats
    ?.map(pixelStatsCategory => pixelStatsCategory?.data || [])
    .flat()
    .filter(pixelStatsItem => !statsItemsToExclude.includes(pixelStatsItem.value))
    .map(pixelStatsItem => ({
      label: pixelStatsItem.value,
      value: pixelStatsItem.value,
      description: `Count: ${pixelStatsItem.count}`
    }))

  // If the list is not empty, add withUnderline: true (border-bottom line) to the last item
  if (list.length > 0) {
    list[list.length - 1] = { ...list[list.length - 1], withUnderline: true }
  }

  return list
}

export const useFetchPromotedObjectFieldsData = (promotedPixelId, campaignObjective = '') => {
  const dispatch = useDispatch()

  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const pixelsWasLoaded = useSelector(choicesPixelsWasLoadedSelector)
  const pixelStats = useSelector(pixelStatsSelector)
  const pixels = useSelector(choicesPixelsSelector)

  const formattedPixels = useMemo(
    () =>
      formatOptionsList({
        list: pixels,
        labelName: 'name',
        valueName: 'id'
      }),
    [pixels]
  )

  const formattedPixelStats = useMemo(() => getFormattedPixelStats(pixelStats), [pixelStats])

  const eventTypeOptions = useMemo(() => {
    const allEventTypes = [...formattedPixelStats, ...customEventTypes]
    if (campaignObjective === OUTCOME_SALES) {
      const campaignObjectiveSalesExclusions = [
        eventTypes.LEAD,
        eventTypes.SUBMIT_APPLICATION,
        eventTypes.FIND_LOCATION,
        eventTypes.SCHEDULE,
        eventTypes.CONTACT,
        eventTypes.CUSTOMIZE_PRODUCT,
        eventTypes.LISTING_INTERACTION,
        eventTypes.FACEBOOK_SELECTED,
        eventTypes.OTHER
      ]
      return allEventTypes.filter(eventType => !campaignObjectiveSalesExclusions.includes(eventType.value))
    } else if (campaignObjective === OUTCOME_LEADS) {
      // if the campaign objective is leads, then disable (but still show) these in the dropdown
      return allEventTypes.map(eventType => {
        return {
          ...eventType,
          isDisabled: !!disabledLeadsObjectiveOptions?.find(disabledOption => disabledOption === eventType.value)
        }
      })
    } else {
      return allEventTypes
    }
  }, [formattedPixelStats, campaignObjective])

  useEffect(() => {
    if (!pixelsWasLoaded) {
      dispatch(getChoicesPixels({ account: selectedAdAccountId }, FACEBOOK_PLATFORM))
    }
  }, [dispatch, selectedAdAccountId, pixelsWasLoaded])

  useEffect(() => {
    if (promotedPixelId) {
      dispatch(getPixelStats(promotedPixelId))
    }
  }, [dispatch, promotedPixelId])

  useEffect(
    () => () => {
      dispatch(clearChoicesPixels())
      dispatch(clearPixelStats())
    },
    [dispatch]
  )

  return {
    eventTypeOptions,
    formattedPixels
  }
}
